/**
 * Whether to send a signed-out visitor straight to the sign-in page.
 *
 * A copy of the account portal's `lib/bounce.ts`, deliberately. The two are
 * separate repos with no shared runtime package between them, and what is being
 * shared is forty lines of reasoning rather than an abstraction — a package
 * boundary would cost more than the duplication does. The KEY differs so the
 * two portals cannot suppress each other's redirect.
 *
 * ── The screen it replaces existed to be clicked through ─────────────
 *
 * Arriving signed out and being told "You need to sign in to use the admin
 * portal" is a page whose only content is a link to the page you wanted. So the
 * portal navigates instead. This module is the part that decides, kept out
 * of the component because the interesting behaviour is a refusal rather than
 * a redirect.
 *
 * ── The refusal: never bounce twice in one tab ───────────────────────
 *
 * An automatic redirect to another origin that fires whenever there is no
 * session is a loop as soon as the round trip fails to produce one — a blocked
 * third-party cookie, a `COOKIE_DOMAIN` that does not cover this host, a
 * session that expires between the two page loads. The browser then shuttles
 * between the portal and the login page indefinitely, and the person sees two
 * pages flashing rather than an error.
 *
 * One timestamp in `sessionStorage`, written immediately before navigating, ends that:
 * the second arrival renders the honest screen with a link on it, which is also
 * the screen that tells somebody debugging what actually happened. Per TAB
 * (`sessionStorage`, not `localStorage`) so a stuck flag cannot outlive the
 * visit, and per ORIGIN, which is what makes it survive the trip to the login
 * page and back.
 *
 * ── Signing out sets it too ──────────────────────────────────────────
 *
 * Otherwise pressing Sign out reloads, finds no session, and shoves the
 * person straight back into the sign-in form — undoing, instantly and
 * invisibly, the thing they just asked for.
 *
 * ── Storage failing means DO NOT bounce ──────────────────────────────
 *
 * `sessionStorage` throws in some privacy modes rather than returning null.
 * With no flag to write there is no way to stop at one redirect, so the safe
 * default is the screen with the link — the old behaviour, for the browsers
 * that cannot support the new one.
 */
const KEY = 'rw-admin-no-bounce';

/**
 * How long a refusal lasts.
 *
 * -- IT USED TO LAST THE WHOLE TAB, AND THAT WAS THE BUG --------------
 *
 * The flag was written as `'1'` and only ever cleared by `allowBounce` when a
 * session existed. So one suppressed bounce -- an abandoned sign-in, or simply
 * pressing Sign out -- left the tab unable to redirect for the rest of its
 * life. Coming back to the portal an hour later showed the screen with the link
 * on it, which is exactly the dead end this module was written to remove.
 *
 * Jon hit it going from one portal to the other (2026-09-09) and asked the
 * right question: if somebody is signed out, the login screen IS where they are
 * going. The suppression should only be stopping a LOOP.
 *
 * A loop is two redirects in the same breath -- the round trip fails to produce
 * a session and the browser shuttles. Ten seconds is far longer than that
 * round trip and far shorter than any deliberate return, so it separates the
 * two cleanly without having to tell them apart any other way.
 */
const SUPPRESS_MS = 10_000;

/** True when a signed-out visitor should be navigated to sign-in. */
export function shouldBounce(
  store: Storage | undefined = safeStore(),
  now: number = Date.now(),
): boolean {
  if (!store) return false;
  try {
    const stamped = store.getItem(KEY);
    if (!stamped) return true;
    const at = Number(stamped);
    /**
     * An unparseable value is treated as LONG AGO, not as forever. That covers
     * the `'1'` this used to write -- a tab open across the deploy would
     * otherwise inherit the old permanent refusal and never bounce again.
     */
    if (!Number.isFinite(at)) return true;
    return now - at > SUPPRESS_MS;
  } catch {
    return false;
  }
}

/** Stop this tab bouncing AGAIN IMMEDIATELY — written before navigating, so a
 *  round trip that fails to produce a session stops at one redirect rather than
 *  shuttling. It lapses; see `SUPPRESS_MS`. */
export function suppressBounce(
  store: Storage | undefined = safeStore(),
  now: number = Date.now(),
): void {
  try {
    store?.setItem(KEY, String(now));
  } catch {
    // Nothing to do. `shouldBounce` fails closed for the same reason.
  }
}

/** A session exists, so the next signed-out arrival is a fresh one and may
 *  bounce again. Without this, one expired session makes the tab show the
 *  link screen for the rest of its life. */
export function allowBounce(store: Storage | undefined = safeStore()): void {
  try {
    store?.removeItem(KEY);
  } catch {
    // See above.
  }
}

/**
 * "I pressed Sign out" is a DIFFERENT state from "I arrived with no session",
 * and they want opposite treatment.
 *
 *   pressed Sign out       -> say so, then carry on to sign-in. The person is
 *                             signed out and the sign-in page is where they are
 *                             going; a screen whose only content is a link to it
 *                             is the dead end this module exists to remove.
 *   arrived with no session -> may redirect once, and must then stop, or it is
 *                             the loop `suppressBounce` exists to prevent.
 *
 * Ported from the account portal, which already made this distinction while
 * this portal dead-ended on Sign out (Jon, 2026-09-09). The two portals now
 * behave the same way, which is worth more than either behaviour on its own --
 * they are the same product to the person using them.
 *
 * Read-then-cleared rather than left set, so the redirect happens once: if the
 * trip back produces no session, the next arrival is the second case above and
 * gets the screen with the diagnosis on it.
 */
const SIGNED_OUT = 'rw-admin-signed-out';

/** Record that this sign-out was asked for, not merely observed. */
export function markSignedOut(store: Storage | undefined = safeStore()): void {
  try {
    store?.setItem(SIGNED_OUT, '1');
  } catch {
    // Same failure posture as suppressBounce: no flag means the honest screen.
  }
}

/** True when the person pressed Sign out to get here. */
export function signedOutDeliberately(store: Storage | undefined = safeStore()): boolean {
  if (!store) return false;
  try {
    return store.getItem(SIGNED_OUT) === '1';
  } catch {
    return false;
  }
}

/** Spend it. Called as the redirect leaves, so a failed trip back does not repeat. */
export function clearSignedOut(store: Storage | undefined = safeStore()): void {
  try {
    store?.removeItem(SIGNED_OUT);
  } catch {
    // See above.
  }
}

function safeStore(): Storage | undefined {
  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
}
